import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal, Alert, ActivityIndicator } from 'react-native';
import AppTextInput from './AppTextInput';
import ModernCard from './ModernCard';
import ModernButton from './ModernButton';
import { STRIPE_CONNECT_APPROVAL_CHECKLIST } from '../constants/stripeConnectApprovalChecklist';
import {
  adminApproveStripeConnectRequest,
  adminRejectStripeConnectRequest,
  fetchPendingStripeConnectRequests,
  StripeConnectRequestListItem,
} from '../services/stripeConnectApprovalService';
import { isAdminApiConfigured } from '../services/adminApiService';

const SuperAdminStripeConnectPanel: React.FC = () => {
  const [requests, setRequests] = useState<StripeConnectRequestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StripeConnectRequestListItem | null>(null);
  const [checklist, setChecklist] = useState<Record<string, boolean>>({});
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [acting, setActing] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchPendingStripeConnectRequests();
      setRequests(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const allChecklistChecked = useMemo(
    () => STRIPE_CONNECT_APPROVAL_CHECKLIST.every((item) => checklist[item.id]),
    [checklist]
  );

  const openReview = (item: StripeConnectRequestListItem) => {
    setSelected(item);
    setChecklist({});
    setAdminNotes('');
    setRejectionReason('');
    setShowReject(false);
  };

  const closeReview = () => {
    setSelected(null);
    setShowReject(false);
  };

  const toggleCheck = (id: string) => {
    setChecklist((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleApprove = async () => {
    if (!selected) return;
    if (!allChecklistChecked) {
      Alert.alert('Checklist incomplete', 'Check every item in the approval checklist.');
      return;
    }
    setActing(true);
    try {
      const result = await adminApproveStripeConnectRequest(
        selected.businessId,
        checklist,
        adminNotes,
        'super_admin'
      );
      if (result.success) {
        Alert.alert('Approved', `${selected.businessName} may now connect Stripe.`);
        closeReview();
        await load();
      } else {
        Alert.alert('Approve failed', result.error || 'Unknown error');
      }
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!rejectionReason.trim()) {
      Alert.alert('Reason required', 'Enter a rejection reason for the seller.');
      return;
    }
    setActing(true);
    try {
      const result = await adminRejectStripeConnectRequest(
        selected.businessId,
        rejectionReason.trim(),
        'super_admin'
      );
      if (result.success) {
        Alert.alert('Rejected', 'The seller will see your reason and may resubmit.');
        closeReview();
        await load();
      } else {
        Alert.alert('Reject failed', result.error || 'Unknown error');
      }
    } finally {
      setActing(false);
    }
  };

  const adminKeyConfigured = isAdminApiConfigured();

  return (
    <View style={styles.wrap}>
      <Text style={styles.sectionTitle}>Stripe Connect access requests</Text>
      <Text style={styles.sectionSubtitle}>
        Review sellers before they can open Stripe onboarding. See{' '}
        <Text style={styles.docHint}>docs/STRIPE_CONNECT_ADMIN_CHECKLIST.md</Text> in the repo.
      </Text>

      {!adminKeyConfigured ? (
        <View style={styles.warnBox}>
          <Text style={styles.warnText}>
            Set REACT_APP_BOOKAPT_ADMIN_API_KEY (web build) to match server BOOKAPT_ADMIN_API_KEY
            to load Stripe requests and approve or reject from this screen.
          </Text>
        </View>
      ) : null}

      {loading ? (
        <ActivityIndicator style={{ marginVertical: 24 }} />
      ) : requests.length === 0 ? (
        <ModernCard style={styles.emptyCard}>
          <Text style={styles.emptyTitle}>No pending requests</Text>
          <Text style={styles.emptySubtitle}>
            When sellers submit payment access requests, they appear here.
          </Text>
        </ModernCard>
      ) : (
        requests.map((item) => (
          <ModernCard key={item.businessId} style={styles.requestCard}>
            <Text style={styles.requestName}>{item.businessName}</Text>
            <Text style={styles.requestMeta}>ID: {item.businessId}</Text>
            {item.approval.requestSnapshot?.email ? (
              <Text style={styles.requestMeta}>Email: {item.approval.requestSnapshot.email}</Text>
            ) : null}
            {item.approval.requestSnapshot?.phone ? (
              <Text style={styles.requestMeta}>Phone: {item.approval.requestSnapshot.phone}</Text>
            ) : null}
            {item.approval.requestSnapshot?.industry ? (
              <Text style={styles.requestMeta}>
                Industry: {item.approval.requestSnapshot.industry}
              </Text>
            ) : null}
            <Text style={styles.requestMeta}>
              Requested:{' '}
              {item.approval.requestedAt
                ? new Date(item.approval.requestedAt).toLocaleString()
                : '—'}
            </Text>
            {item.approval.requestNote ? (
              <Text style={styles.requestNote}>Note: {item.approval.requestNote}</Text>
            ) : null}
            <Text style={styles.requestMeta}>
              Terms: {item.approval.termsVersion || '—'}
            </Text>
            <View style={styles.row}>
              <ModernButton
                title="Review"
                variant="primary"
                size="small"
                onPress={() => openReview(item)}
                style={styles.flexBtn}
              />
            </View>
          </ModernCard>
        ))
      )}

      <Modal visible={!!selected} animationType="slide" onRequestClose={closeReview}>
        <View style={styles.modalContainer}>
          <ScrollView contentContainerStyle={styles.modalScroll}>
            <Text style={styles.modalTitle}>Review payment access</Text>
            <Text style={styles.modalBusiness}>{selected?.businessName}</Text>
            <Text style={styles.modalId}>{selected?.businessId}</Text>

            {selected?.approval.requestSnapshot ? (
              <View style={styles.snapshotBox}>
                <Text style={styles.snapshotTitle}>Business snapshot</Text>
                {Object.entries(selected.approval.requestSnapshot).map(([k, v]) =>
                  v ? (
                    <Text key={k} style={styles.snapshotLine}>
                      {k}: {String(v)}
                    </Text>
                  ) : null
                )}
              </View>
            ) : null}

            <Text style={styles.checklistHeading}>Approval checklist (all required)</Text>
            {STRIPE_CONNECT_APPROVAL_CHECKLIST.map((item) => (
              <TouchableOpacity
                key={item.id}
                style={styles.checkRow}
                onPress={() => toggleCheck(item.id)}
                activeOpacity={0.7}
              >
                <View
                  style={[styles.checkBox, checklist[item.id] && styles.checkBoxOn]}
                >
                  {checklist[item.id] ? <Text style={styles.checkMark}>✓</Text> : null}
                </View>
                <View style={styles.checkTextWrap}>
                  <Text style={styles.checkTitle}>{item.title}</Text>
                  <Text style={styles.checkDesc}>{item.description}</Text>
                </View>
              </TouchableOpacity>
            ))}

            <Text style={styles.label}>Admin notes (internal)</Text>
            <AppTextInput
              style={styles.textArea}
              multiline
              value={adminNotes}
              onChangeText={setAdminNotes}
              placeholder="Optional notes for audit trail"
            />

            {!showReject ? (
              <View style={styles.modalActions}>
                <ModernButton
                  title={acting ? '…' : 'Approve'}
                  variant="primary"
                  onPress={handleApprove}
                  disabled={acting || !allChecklistChecked || !adminKeyConfigured}
                  style={styles.flexBtn}
                />
                <ModernButton
                  title="Reject…"
                  variant="error"
                  onPress={() => setShowReject(true)}
                  disabled={acting}
                  style={styles.flexBtn}
                />
                <ModernButton
                  title="Cancel"
                  variant="outline"
                  onPress={closeReview}
                  disabled={acting}
                />
              </View>
            ) : (
              <>
                <Text style={styles.label}>Rejection reason (shown to seller)</Text>
                <AppTextInput
                  style={styles.textArea}
                  multiline
                  value={rejectionReason}
                  onChangeText={setRejectionReason}
                  placeholder="Explain what the seller must fix"
                />
                <View style={styles.modalActions}>
                  <ModernButton
                    title="Confirm reject"
                    variant="error"
                    onPress={handleReject}
                    disabled={acting}
                    style={styles.flexBtn}
                  />
                  <ModernButton
                    title="Back"
                    variant="outline"
                    onPress={() => setShowReject(false)}
                    disabled={acting}
                  />
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    paddingHorizontal: 20,
    paddingBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1C1C1E',
    marginBottom: 6,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#6C6C70',
    lineHeight: 20,
    marginBottom: 12,
  },
  docHint: {
    fontWeight: '600',
    color: '#007AFF',
  },
  warnBox: {
    backgroundColor: '#fff3cd',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  warnText: {
    fontSize: 13,
    color: '#856404',
    lineHeight: 18,
  },
  emptyCard: {
    padding: 16,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6C6C70',
    marginTop: 4,
  },
  requestCard: {
    marginBottom: 12,
    padding: 14,
  },
  requestName: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1C1C1E',
  },
  requestMeta: {
    fontSize: 13,
    color: '#6C6C70',
    marginTop: 4,
  },
  requestNote: {
    fontSize: 14,
    color: '#1C1C1E',
    marginTop: 8,
    fontStyle: 'italic',
  },
  row: {
    flexDirection: 'row',
    marginTop: 12,
    gap: 8,
  },
  flexBtn: {
    flex: 1,
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingTop: 48,
  },
  modalScroll: {
    padding: 20,
    paddingBottom: 48,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  modalBusiness: {
    fontSize: 18,
    fontWeight: '600',
  },
  modalId: {
    fontSize: 12,
    color: '#6C6C70',
    marginBottom: 16,
  },
  snapshotBox: {
    backgroundColor: '#fff',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  snapshotTitle: {
    fontWeight: '700',
    marginBottom: 8,
  },
  snapshotLine: {
    fontSize: 14,
    marginBottom: 4,
  },
  checklistHeading: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  checkRow: {
    flexDirection: 'row',
    marginBottom: 14,
    alignItems: 'flex-start',
  },
  checkBox: {
    width: 22,
    height: 22,
    borderWidth: 2,
    borderColor: '#adb5bd',
    borderRadius: 4,
    marginRight: 10,
    marginTop: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkBoxOn: {
    backgroundColor: '#34C759',
    borderColor: '#34C759',
  },
  checkMark: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
  },
  checkTextWrap: {
    flex: 1,
  },
  checkTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1C1C1E',
  },
  checkDesc: {
    fontSize: 13,
    color: '#6C6C70',
    lineHeight: 18,
    marginTop: 2,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginTop: 12,
    marginBottom: 6,
  },
  textArea: {
    borderWidth: 1,
    borderColor: '#ced4da',
    borderRadius: 8,
    padding: 12,
    minHeight: 80,
    backgroundColor: '#fff',
    textAlignVertical: 'top',
  },
  modalActions: {
    marginTop: 20,
    gap: 10,
  },
});

export default SuperAdminStripeConnectPanel;
